import * as yup from "yup";

export const LoginSchema = yup.object({
  email: yup
    .string()
    .email("Please enter a valid email address.")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address."
    )
    .required("Email is required."),
  password: yup.string().required("Password is required."),
});
export const OnboardigSchema = yup?.object({
  businessName: yup.string().required("Company Name is required."),
  type: yup.string().required("Type is required."),
  branch: yup.string(),
  title: yup.string(),
  firstName: yup.string().required("First Name is required."),
  lastName: yup.string().required("Last Name is required."),
  mobileNo: yup
    .string()
    .matches(/^[0-9]{10}$/, "Mobile Number must be exactly 10 digits.")
    .required("Mobile Number is required."),
  emailId: yup
    .string()
    .email("Invalid Email.")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address."
    )
    .required("Email ID is required."),
  alternateEmailId: yup
    .string()
    .email("Invalid Alternate Email.")
    .required("Alternate Email ID is required."),
  address: yup.string().required("Address is required."),
  phoneNo: yup
    .string()
    .matches(/^[0-9]+$/, "Landline Number must be numeric.")
    .required("Landline Number is required."),
  postalCode: yup
    .string()
    .matches(/^[0-9]{5,6}$/, "Postal Code must be 5 or 6 digits.")
    .required("Postal Code is required"),
  documentProofEmirates: yup?.string().required("Document Proof is Required."),
  documentProofTradeLicense: yup
    ?.string()
    .required("Document Proof is Required."),
  corporateLogo: yup?.string().required("Corporate Logo Proof is Required."),
});

export const flightSearchSchema = yup.object().shape({
  tripType: yup
    .string()
    .oneOf(["One way", "Round Trip", "Multi-city"])
    .required("Trip type is required."),

  tripTypeNumber: yup.number().min(0).required("Trip type number is required."),

  origin: yup.string().required("Origin is required."),

  destination: yup
    .string()
    .required("Destination is required.")
    .test(
      "different-from-origin.",
      "Origin and destination cannot be the same.",
      function (dest) {
        const { origin } = this.parent;
        if (!origin || !dest) return true; // skip check if either missing
        return origin !== dest;
      }
    ),

  fromDate: yup.string().required("Departure date is required."),

  toDate: yup.string().when(["tripType", "fromDate"], {
    is: (tripType: string, fromDate: string) =>
      tripType === "Round Trip" && !!fromDate,
    then: (schema) =>
      schema
        .required("Return date is required.")
        .test(
          "is-after-fromDate.",
          "Return date cannot be before departure date.",
          function (toDate) {
            const { fromDate } = this.parent;
            return !toDate || new Date(toDate) >= new Date(fromDate);
          }
        ),
    otherwise: (schema) => schema.notRequired(),
  }),

  travellersDetail: yup.string().required("Traveller info is required."),

  airportName: yup.string(),

  infants: yup.number().min(0, "Infants cannot be negative.").required(),
  child: yup.number().min(0, "Children count cannot be negative.").required(),
  adults: yup.number().min(1, "At least one adult is required.").required(),
});

export const createPasswordSchema = yup.object().shape({
  new_password: yup
    .string()
    .min(8, "Password must be at least 8 characters.")
    .required("New Password is required."),
  confirm_password: yup
    .string()
    .oneOf([yup.ref("new_password")], "Passwords do not match.")
    .required("Confirm Password is required."),
});

export const ManageBookingSchema = yup.object().shape({
  spnr: yup
    .string()
    .required("S PNR is required.")
    .min(6, "S PNR must be at least 6 characters.")
    .max(10, "S PNR must be less than or equal to 10 characters."),

  groupName: yup
    .string()
    .required("group name is required.")
    .min(30, "group name must be at least 6 characters.")
    .max(40, "group name must be less than or equal to 10 characters."),

  hotelConfirmation: yup
    .string()
    .required("Hotel Confirmation is required.")
    .min(6, "Hotel Confirmation must be at least 6 characters.")
    .max(10, "Hotel Confirmation must be less than or equal to 10 characters."),

  customerName: yup
    .string()
    .required("Customer Name is required.")
    .min(2, "Customer Name must be at least 2 characters.")
    .max(100, "Customer Name must be less than or equal to 100 characters."),

  firstName: yup
    .string()
    .required("First Name is required.")
    .min(2, "First Name must be at least 2 characters.")
    .max(50, "First Name must be less than or equal to 50 characters."),

  lastName: yup
    .string()
    .required("Last Name is required.")
    .min(2, "Last Name must be at least 2 characters.")
    .max(50, "Last Name must be less than or equal to 50 characters."),
  searchBy: yup
    .string()
    .required("Search By is required.")
    .oneOf(
      ["All", "Live", "To be Cancelled", "Cancelled"],
      "Invalid search option."
    ),

  fromDate: yup
    .date()
    .required("Departure Date is required.")
    .min(new Date(), "Departure Date cannot be in the past."),

  toDate: yup.lazy((value, context) => {
    const tripType = context.parent.tripType;

    if (tripType === "roundTrip") {
      return yup
        .date()
        .required("Return date is required for round trip.")
        .min(yup.ref("fromDate"), "Return Date cannot be before From Date.");
    } else {
      return yup.date().nullable().notRequired();
    }
  }),
});

export const CreateUserSchema = yup.object().shape({
  title: yup.string().required("Title is required."),
  firstName: yup
    .string()
    .matches(/^[A-Za-z]+$/, "First name must contain only letters.")
    .required("First name is required."),
  lastName: yup
    .string()
    .matches(/^[A-Za-z]+$/, "Last name must contain only letters.")
    .required("Last name is required."),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required."),
  mobileNumber: yup.string().required("Mobile number is required."),
  aliasName: yup.string(),
  employeeCode: yup
    .string()
    .matches(
      /^[A-Za-z0-9]+$/,
      "Employee code can contain only letters and numbers."
    )
    .required("Employee code is required"),
  // branch: yup.string().required("Branch is required"),
  // role: yup.string().required("Role is required"),
  status: yup.string().required("Status is required."),
  // corporateName: yup.string().required("Coorporate Name is required."),
});

export const HotelBookingSchema = yup.object({
  location: yup
    .string()
    .required("Location is required.")
    .min(3, "Location must be at least 3 characters long."),

  checkIn: yup
    .date()
    .required("Check-In date is required.")
    .min(new Date(), "Check-In date cannot be in the past."),

  checkOut: yup
    .date()
    .required("Check-Out date is required.")
    .min(yup.ref("checkIn"), "Check-Out date must be after Check-In date."),

  nights: yup.string().required("Number of nights is required."),

  nationality: yup.string().required("Nationality is required."),

  residentCountry: yup.string().required("Resident Country is required."),

  addRooms: yup.string().required("Please select the number of rooms."),

  selectCustomerName: yup.string().required("Please select customer type."),
});
// Base traveler schema with common fields
const baseTravelerSchema = {
  Title: yup.string().required("Please select a title."),
  FirstName: yup
    .string()
    .required("First name is required")
    .matches(/^[a-zA-Z ]+$/, "Only alphabets are allowed."),
  LastName: yup
    .string()
    .required("Last name is required.")
    .matches(/^[a-zA-Z ]+$/, "Only alphabets are allowed."),
  isInternational: yup.boolean().default(false),
  DOB: yup
    .date()
    .max(new Date(), "Date of birth cannot be in the future.")
    .when("isInternational", {
      is: true,
      then: (schema) =>
        schema.required("Date of birth is required for international travel."),
    }),
  PassportNo: yup.string().when("isInternational", {
    is: true,
    then: (schema) =>
      schema
        .required("Passport number is required.")
        .matches(/^[A-Z0-9]{6,9}$/, "Invalid passport number format."),
  }),
  PassportExpiryDate: yup.date().when("isInternational", {
    is: true,
    then: (schema) =>
      schema
        .required("Passport expiry date is required.")
        .min(new Date(), "Passport must be valid (not expired)"),
  }),
};

// Adult traveler schema (extends base with additional required fields)
const adultSchema = yup.object({
  ...baseTravelerSchema,
  EmailAddress: yup
    .string()
    .required("Email is required.")
    .email("Please enter a valid email address."),
  MobileNumber: yup
    .string()
    .required("Mobile number is required.")
    .matches(/^[0-9]+$/, "Must contain only numbers.")
    .min(10, "Must be at least 10 digits."),
  countryName: yup.string().required("Country is required."),
  nationality: yup.string().required("Nationality is required."),
});

// Child traveler schema (extends base)
const childSchema = yup.object({
  ...baseTravelerSchema,
  DOB: yup
    .date()
    .max(new Date(), "Date of birth cannot be in the future.")
    .when("isInternational", {
      is: true,
      then: (schema) =>
        schema.required("Date of birth is required for international travel."),
    }),
});

// Infant traveler schema (extends base with stricter age validation)
const infantSchema = yup.object({
  ...baseTravelerSchema,
  DOB: yup
    .date()
    .max(new Date(), "Date of birth cannot be in the future.")
    .required("Infant's date of birth is required.")
    .test(
      "is-infant",
      "Must be under 2 years to be considered an infant.",
      (value) => {
        if (!value) return false;
        const age = new Date().getFullYear() - new Date(value).getFullYear();
        return age < 2;
      }
    ),
});

// Main schema builder function
export const getTravelersValidationSchema = (
  adultCount: number,
  childCount: number,
  infantCount: number
) => {
  return yup.object({
    adultTravellers: yup
      .array()
      .of(adultSchema)
      .min(adultCount, `Please provide details for all ${adultCount} adult(s)`)
      .max(adultCount, `Maximum ${adultCount} adult(s) allowed.`)
      .required("At least one adult traveler is required."),

    ChildTraveller: yup
      .array()
      .of(childSchema)
      .when([], {
        is: () => childCount > 0,
        then: (schema) =>
          schema
            .min(
              childCount,
              `Please provide details for all ${childCount} child(ren)`
            )
            .max(childCount, `Maximum ${childCount} child(ren) allowed.`)
            .required(),
        otherwise: () => yup.array().notRequired(),
      }),

    InfantTraveller: yup
      .array()
      .of(infantSchema)
      .when([], {
        is: () => infantCount > 0,
        then: (schema) =>
          schema
            .min(
              infantCount,
              `Please provide details for all ${infantCount} infant(s)`
            )
            .max(infantCount, `Maximum ${infantCount} infant(s) allowed.`)
            .required(),
        otherwise: () => yup.array().notRequired(),
      }),
  });
};


export const CorporateManagementSchema = yup
  .object()
  .shape({
    business_name: yup.string().trim().required("Business name is required."),

    type: yup.string().trim().required("Type is required."),
    branch: yup.string().trim().required("Branch is required."),
    title: yup.string().trim().required("Title is required."),
    firstName: yup.string().trim().required("First name is required."),
    lastName: yup.string().trim().required("Last name is required."),
    address: yup.string().trim().required("Address is required."),

    admin_email: yup
      .string()
      .trim()
      .email("Invalid primary email.")
      .required("Primary email is required."),

    email_id_alternate: yup
      .string()
      .test(
        "not-same-as-primary",
        "Alternate email must be different from primary email.",
        function (alternateEmail) {
          const primaryEmail = this.parent.admin_email;
          if (
            alternateEmail &&
            primaryEmail &&
            alternateEmail.trim() === primaryEmail.trim()
          ) {
            return false;
          }
          return true;
        }
      ),

    mobile_no: yup.string().trim().required("Mobile No. is required."),

    phone_number: yup
      .string()
      .required("Phone number is required.")
      .matches(/^\d+$/, "Phone number must contain only digits.")
      .min(10, "Phone number must be at least 10 digits.")
      .max(15, "Phone number must be at most 15 digits."),

    postal_code: yup
      .string()
      .matches(/^[0-9]{6,10}$/, "Postal Code must be between 6 to 10 digits.")
      .required("Postal code is required."),

    selectedCurrency: yup.string().required("Currency is required"),

    trade_license_doc: yup
      .string()
      .nullable()
      .required("Trade license document is required."),

    emirates_id_doc: yup
      .string()
      .nullable()
      .required("Emirates ID document is required."),

    countryId: yup.string().required("Country is required."),
    statesId: yup.string().required("State is required."),
    cityName: yup.string().required("City is required."),
    date: yup.string().required("Date of Joining is required."),

    corporateLogo: yup
      .string()
      .nullable()
      .required("Corporate logo is required."),

    payment_mode: yup.string().required("Payment mode is required."),

    airline_commission_category: yup
      .string()
      .nullable()
      .required("Airline commission category is required."),

    airline_commission: yup
      .string()
      .nullable()
      .required("Airline commission is required."),

    admin_password: yup
      .string()
      .min(6, "Password must be at least 6 characters.")
      .required("Admin password is required."),

    admin_confirm_password: yup
      .string()
      .oneOf([yup.ref("admin_password")], "Passwords must match.")
      .required("Confirm password is required."),

    admin_feature_ids: yup
      .array()
      .of(yup.string())
      .min(1, "At least one permission must be selected."),

    // ✅ Payment mode checkboxes
    istopUpSelected: yup.boolean(),
    isPGSelected: yup.boolean(),
  })
  .test(
    "at-least-one-checked",
    "Please select at least one payment option.",
    function (values) {
      if (values.istopUpSelected || values.isPGSelected) {
        return true;
      }
      // attach error to istopUpSelected so Formik can show message
      return this.createError({ path: "istopUpSelected" });
    }
  );

export const UpdateCustomerBalanceSchema = yup.object({
  paymentMode: yup.string().required("Payment Mode is required."),
  depositDate: yup.date().nullable().required("Deposit Date is required."),
  chequeDate: yup.date().nullable().required("Cheque Date is required."),
  chequeNo: yup.string().required("Cheque No is required."),
  bankName: yup.string().required("Bank Name is required."),
  accountNo: yup.string().required("Account No is required."),
  amount: yup
    .number()
    .typeError("Amount must be a number.")
    .positive("Amount must be positive.")
    .required("Amount is required."),
  remarks: yup.string().required("Remarks are required."),
});

export const ViewAssignCommissionSchema = yup.object({
  basedOn: yup.string().required("Please Select BasedOn."),
});

export const CreateCommissionSchema = yup.object({
  category: yup.string().required("Category is required."),

  categoryName: yup.string().when("category", {
    is: (category: string) => category === "Add new Category.",
    then: (schema) => schema.required("Category Name is required."),
    otherwise: (schema) => schema.notRequired(),
  }),
  commissions: yup.array().of(
    yup.object({
      commissionType: yup.string().required("Type is required."),
      crsName: yup.string().required("CRS Name is required."),
      airport: yup
        .string()
        .required("Airport is required.")
        .test(
          "valid-airport-logic",
          "Invalid airport: destination and origin countries are not valid for selected airport type.",
          function (value) {
            const { originCountries = [], destinationCountries = [] } =
              this.parent;

            if (
              !value ||
              originCountries.length === 0 ||
              destinationCountries.length === 0
            )
              return true; // skip if incomplete

            const hasOverlap = originCountries.some((origin: any) =>
              destinationCountries.includes(origin)
            );

            if (value === "Dom.") return !hasOverlap;
            if (value === "Intl.") return hasOverlap;

            return true; // for empty or other values, skip
          }
        ),
      airportCategory: yup.string().required("Airport category is required."),
      airlineCodes: yup.array().of(yup.string().required()),
      cabin: yup.string().required("Cabin is required."),
      flatType: yup.string().required("Flat type is required."),
      fareType: yup.string().required("Fare type is required."),
      per: yup
        .mixed()
        .test("is-valid-decimal", "Invalid decimal format", (value) => {
          if (value === undefined || value === null || value === "")
            return true;
          return /^[0-9]+(\.[0-9]{1,2})?$/.test(String(value));
        })
        .test("max-10", "Per must be less than or equal to 10", (value) => {
          if (value === undefined || value === null || value === "")
            return true;
          return parseFloat(String(value)) <= 10;
        }),
      adultChildValue: yup
        .number()
        .typeError("Adult value must be a number.")
        .integer("Decimal not allowed.")
        .max(500, "Value must not exceed 500.")
        .min(0, "Value must be 0 or more."),
      infantValue: yup
        .number()
        .typeError("Adult value must be a number.")
        .max(500, "Value must not exceed 500.")
        .min(0, "Value must be 0 or more."),
    })
  ),
});

export const TopupSchema = yup.object().shape({
  amount: yup.string().required("Amount is required."),
  topup_type: yup.string().required("Topup Type is required."),
  payment_mode: yup.string().required("Payment Mode is required."),
  payment_reference: yup.string().required("Payment Reference is required."),
  // user_note: yup.string(),
});

export const ForgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email address.")
    .required("Email is required."),
});

export const CouponFormSchema = yup.object({
  code: yup
    .string()
    .required("Offer code is required.")
    .matches(/^[A-Za-z0-9]+$/, "Offer code must be alphanumeric.")
    .min(4, "Offer code must be at least 4 characters.")
    .max(12, "Offer code must be at most 12 characters."),
  description: yup.string().required("Code description is required."),
  value: yup
    .number()
    .typeError("Value must be a number.")
    .positive("Value must be greater than 0.")
    .required("Value is required."),
  maxUses: yup
    .number()
    .typeError("Max Uses must be a number.")
    .positive("Max Uses be greater than 0.")
    .required("Max Uses is required."),
  maxUsesPerUser: yup
    .number()
    .typeError("Max uses per user must be a number.")
    .positive("Max uses must be greater than 0.")
    .required("Max uses per user is required."),
  minPurchase: yup
    .number()
    .typeError("Min purchase must be a number.")
    .positive("Min purchase  must be greater than 0.")
    .required("Min purchase is required."),
  validFrom: yup
    .date()
    .required("Valid From is required.")
    .typeError("Invalid date format."),

  validUntil: yup
    .date()
    .required("Valid Until is required.")
    .typeError("Invalid date format.")
    .min(
      yup.ref("validFrom"),
      "Valid Until must be the same or after Valid From."
    ),

  couponType: yup.string().required("Coupon type is required."),
  appliesTo: yup.string().required("Applies to is required."),
  status: yup.string().required("Status is required."),
});

export const ChangePasswordvalidationSchema = yup.object().shape({
  oldPassword: yup.string().required("Old password is required."),
  newPassword: yup
    .string()
    .min(
      8,
      "This password is too short. It must contain at least 8 characters."
    )
    .required("New password is required."),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match.")
    .required("Confirm password is required."),
});

export const AdminUservalidationSchema = yup.object({
  title: yup.string().required("Title is required."),
  firstName: yup.string().required("First name is required."),
  lastName: yup.string().required("Last name is required."),
  email: yup.string().email("Invalid email").required("Email is required."),
  mobile: yup
    .string()
    .trim()
    .matches(/^[0-9]{7,15}$/, "Mobile No. must be numeric and 7-15 digits.")
    .required("Mobile No. is required."),
  role: yup.string().required("Role is required."),
  // ✅ password required only in create mode
  password: yup.string().when("$isEditMode", {
    is: false, // if not edit mode (i.e., create)
    then: (schema) =>
      schema
        .required("Password is required.")
        .min(8, "Password must be at least 8 characters."),
    otherwise: (schema) => schema.notRequired(),
  }),

  // ✅ confirmPassword required only if password is entered (or on create)
  confirmPassword: yup.string().when(["password", "$isEditMode"], {
    is: (password: any, isEditMode: any) => !isEditMode && !!password,
    then: (schema) =>
      schema
        .required("Confirm password is required.")
        .oneOf([yup.ref("password")], "Passwords must match."),
    otherwise: (schema) =>
      schema.oneOf([yup.ref("password")], "Passwords must match."),
  }),
});

export const TopupRequestByCorporateUserSchema = yup.object().shape({
  amount: yup
    .number()
    .typeError("Amount must be a number.")
    .positive("Amount must be greater than 0.")
    .required("Amount is required."),

  priority: yup.string().required("Priority is required."),
});

export const CorporatePostpaidAccountSchema = yup.object().shape({
  corporateName: yup.string().required("Corporate name is required."),
  creditLimit: yup
    .number()
    .typeError("Credit limit must be a number.")
    .required("Credit limit is required.")
    .min(0, "Credit limit must be positive."),
});
